/**
 * Safe sorting helper utilities for Ant Design table columns.
 * Prevents TypeError exceptions during sorting caused by null, undefined,
 * numeric values, or invalid dates.
 */

/**
 * Safe string comparator for table column sorters.
 * Handles null, undefined, numbers, and booleans gracefully without throwing.
 *
 * @param {any} a
 * @param {any} b
 * @returns {number}
 */
export const safeStringSort = (a, b) => {
  const strA = a === null || a === undefined ? "" : String(a);
  const strB = b === null || b === undefined ? "" : String(b);
  return strA.localeCompare(strB);
};

/**
 * Safe numeric comparator for table column sorters.
 * Handles numbers, numeric strings (including comma-separated values like "1,00,000"),
 * booleans, null, undefined, and non-numeric values without throwing.
 *
 * @param {any} a
 * @param {any} b
 * @returns {number}
 */
export const safeNumberSort = (a, b) => {
  const parse = (val) => {
    if (val === null || val === undefined || val === "") return 0;
    if (typeof val === "boolean") return val ? 1 : 0;
    if (typeof val === "number") return Number.isFinite(val) ? val : 0;
    const num = Number(String(val).replace(/,/g, "").trim());
    return Number.isFinite(num) ? num : 0;
  };
  return parse(a) - parse(b);
};

/**
 * Safe date comparator for table column sorters.
 * Handles ISO date strings, Date objects, null, undefined, and invalid dates without throwing.
 *
 * @param {any} a
 * @param {any} b
 * @returns {number}
 */
export const safeDateSort = (a, b) => {
  const parse = (val) => {
    if (!val) return 0;
    const time = new Date(val).getTime();
    return Number.isFinite(time) ? time : 0;
  };
  return parse(a) - parse(b);
};

/**
 * Safe alphanumeric ID comparator for table column sorters.
 * Extracts numeric digits from strings (e.g. "PRV1000001", "UID12", "P2") for natural order,
 * falling back to string comparison when numbers are absent or equal.
 *
 * @param {any} a
 * @param {any} b
 * @returns {number}
 */
export const safeIdSort = (a, b) => {
  const numA = parseInt(String(a || "").replace(/\D/g, ""), 10);
  const numB = parseInt(String(b || "").replace(/\D/g, ""), 10);
  const validA = Number.isFinite(numA) ? numA : 0;
  const validB = Number.isFinite(numB) ? numB : 0;
  if (validA !== validB) {
    return validA - validB;
  }
  return safeStringSort(a, b);
};
